/**
 * Aufmaß Export — lädt das Aufmaß-/Mengenermittlungs-PDF (Soll-/Ist-/
 * Restmengen je LV-Position) vom Server herunter.
 *
 * Sekundärer Export-Button im Stil der übrigen Export-Komponenten
 * (urkalkulation-export.tsx). Wird vom Orchestrator eingebunden,
 * nicht direkt in der Sidebar.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Ruler, Check, Loader2 } from 'lucide-react';
import { useOfferStore } from '@/lib/store';
import { useToast } from '@/hooks/use-toast';

export default function AufmassExport() {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const quoteHeader = useOfferStore((state) => state.quoteHeader);
  const quoteItems = useOfferStore((state) => state.quoteItems);
  const gewerkOrder = useOfferStore((state) => state.gewerkOrder);

  const handleExport = async () => {
    if (quoteItems.length === 0) return;

    setIsExporting(true);
    setExportSuccess(false);

    try {
      const response = await fetch('/api/aufmass/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header: quoteHeader,
          items: quoteItems,
          gewerkOrder,
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: `Server-Fehler ${response.status}` }));
        throw new Error(errorData.error || `Server-Fehler ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const offerNumber = quoteHeader.angebot_nr?.trim() || `Angebot-${Date.now()}`;
      link.download = `Aufmass-${offerNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportSuccess(true);
      toast({
        title: 'Aufmaß exportiert',
        description: `"${link.download}" wurde heruntergeladen.`,
        variant: 'success',
      });
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      console.error('Aufmaß export failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      toast({
        title: 'Aufmaß fehlgeschlagen',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outline"
      onClick={handleExport}
      disabled={isExporting || quoteItems.length === 0}
      className="w-full text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
      data-testid="button-export-aufmass"
    >
      {isExporting ? (
        <>
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Aufmaß wird erstellt...
        </>
      ) : exportSuccess ? (
        <>
          <Check className="h-3 w-3 mr-1" />
          Aufmaß bereit
        </>
      ) : (
        <>
          <Ruler className="h-3 w-3 mr-1" />
          Aufmaß (PDF)
        </>
      )}
    </Button>
  );
}
